import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import claims from "../../../gql/claims";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import AppButton from "../../../shared/AppButton";
import CustomFilterInput from "../../../shared/CustomFilterInput";

const BulkCancellationSchema = Yup.object().shape({
  batch_no: Yup.string().required("Batch Number is required"),
  status: Yup.array()
    .of(Yup.string().required("Each status must be a string"))
    .required("Status is required")
    .min(1, "At least one status is required"),
  limit: Yup.number()
    .integer("Limit must be an integer")
    .positive("Limit must be a positive number")
    .required("Limit is required"),
});

export default function BulkCancellation({ showLoader, setShowLoader }) {
  const { t } = useTranslation();

  const [submitEntry, { loading: submitLoading }] = useMutation(
    claims.BULK_CANCELLATION,
    {
      update(proxy, result) {
        setShowLoader(false);
        onSuccessfulRequestResponse(result.data);
      },
      onError(error) {
        setShowLoader(false);
        onResponseError(error.graphQLErrors[0]);
      },
    },
  );

  const onSuccessfulRequestResponse = (data) => {
    if (data?.bulkCancelRequest) {
      toast.success(data?.bulkCancelRequest?.message);
    } else {
      toast.error(t("An error occurred while cancelling the claims."));
    }
  };

  const onResponseError = (error) => {
    toast.error(`${error?.message}`);
  };

  const statusOptions = [
    { value: "pended", label: t("Pended") },
    { value: "rejected", label: t("Rejected") },
    { value: "approved", label: t("Approved") },
    { value: "partial Approved", label: t("Partial Approved") },
    { value: "pending", label: t("Pending") },
  ];

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "28px",
      height: "28px",
      fontSize: "12px",
      borderRadius: "6px",
      backgroundColor: "rgba(241, 245, 249, 0.5)",
    }),
    valueContainer: (base) => ({
      ...base,
      height: "30px",
      padding: "0 6px",
      display: "flex",
      alignItems: "center",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "30px",
    }),
    multiValue: (base) => ({
      ...base,
      margin: "1px",
    }),
  };

  const handleBulkCancellation = async (values, { resetForm }) => {
    setShowLoader(true);
    try {
      await submitEntry({
        variables: {
          input: {
            batch_no: values.batch_no,
            limit: parseInt(values.limit),
            status: values.status,
          },
        },
      });
      resetForm();
    } catch (error) {
      console.error(t("Error submitting bulk cancellation:"), error);
    }
  };

  return (
    <Formik
      initialValues={{ batch_no: "", limit: "", status: [] }}
      validationSchema={BulkCancellationSchema}
      onSubmit={handleBulkCancellation}
    >
      {({
        errors,
        touched,
        values,
        handleChange,
        handleBlur,
        setFieldValue,
      }) => (
        <Form>
          <div className="w-1/2 bg-white p-2 rounded-lg border border-slate-200">
            <div className="details-fields grid grid-cols-1 md:grid-cols-2 gap-x-1.5 gap-y-1.5 mb-1.5 pb-1.5 border-b">
              <CustomFilterInput
                label={t("Batch Number")}
                name="batch_no"
                placeholder={t("Enter Batch number")}
                value={values.batch_no}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.batch_no}
                touched={touched.batch_no}
              />
              <CustomFilterInput
                label={t("Limit (recommended 100)")}
                name="limit"
                placeholder={t("Enter Limit")}
                value={values.limit}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.limit}
                touched={touched.limit}
              />
              <div className="flex flex-col md:col-span-2">
                <label className="block text-xs text-gray-700 mb-0.5 capitalize">
                  {t("Statuses")}
                </label>
                <Select
                  isMulti
                  name="status"
                  options={statusOptions}
                  styles={customSelectStyles}
                  placeholder={t("Select Statuses")}
                  onChange={(selected) =>
                    setFieldValue(
                      "status",
                      selected ? selected.map((o) => o.value) : [],
                    )
                  }
                  onBlur={() => handleBlur({ target: { name: "status" } })}
                />
                {errors.status && touched.status && (
                  <p className="text-[10px] text-red-500 mt-1">
                    {errors.status}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-end mb-0.5 justify-end">
              <AppButton
                label={t("Submit")}
                startIcon={<CloseIcon />}
                type="submit"
                isLoading={submitLoading}
                loadingText={t("Submitting")}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
